import { PermissionResponse } from '@business/entities/permission';
import { TrayItemType } from '@modules/dashboard/DashboardPage/DashboardToolbar';
import { BaseResponse, PageQuery } from '../common';
import { CompanyResponse } from '../companies/CompanyResponse';
import { TeamId } from '../team';
import { CustomFieldResponseType } from '../integration';
import { AccountSettingsResponse } from '../settings';
import { OrgId } from '../organization';
import { ContactId } from '../contact';
import { RegionId } from '../tm/TmRegionResponse';
import { VeryCompactProviderResponse } from '../tm';

export enum UserStatus {
  active = 0,
  inactive = 1,
  dontknow = 2,
  dontknow2 = 3,
}

export enum UserRole {
  admin = 1,
  // client = 2,
  user = 3,
  // affiliate = 4,
  // noLogin = 5,
  subcontractor = 6,
  ClientPortal = 7,
  salesRep = 8,
  estimator = 9,
}
export enum SubcontractorStatus {
  inactive = '0',
  active = '1',
  invited = '2',
  awaitingSubmission = '3',
  underReview = '4',
  revisionRequired = '5',
  applicationApproved = '6',
  applicationRejected = '7',
  reviewRejected = '8',
}
export enum SubcontractorFormValueStatus {
  approved = 0,
  reviewRejected = 1,
  rejected = 2,
  underReview = 4,
}

export type UserId = string;
export type UserEmail = string;
export type UserSocialId = string;

export interface UserSettingsResponse {
  trayItems?: TrayItemType[];
}

export interface AccountResponse {
  title: string;
  appId: string;
  logo?: string;
}

export enum UserType {
  fullTime = 'fullTime',
  partTime = 'partTime',
  contractor = 'contractor',
  asNeeded = 'asNeeded',
}
export interface UserResponse extends BaseResponse {
  categoryIds: string;
  id: UserId;

  token?: string;

  accountStatus: string;
  firstName: string;
  lastName: string;
  email: UserEmail;
  salary?: number;
  weeklyQuota?: number | null;
  /** Employee id in Paylocity (commission payroll download); only sent to admins */
  paylocityId?: string | null;
  jobTitle?: string;
  personalEmail?: string;
  phone: string;
  resellerId: number;
  activation: string;
  subcontractorStatus: SubcontractorStatus;
  /** Subcontractors only: company names they work under (provider name, else vendor name). */
  companyNames?: string[];
  status: UserStatus;
  avatar: string | null | File;
  signature: string;
  companies: CompanyResponse[];
  socialAccounts: UserSocialAccountResponse[];
  settings: UserSettingsResponse;
  role: UserRole;
  regionId: RegionId;
  password?: string;
  signatures: ChannelSignatureResponse[];
  accounts: AccountResponse[];
  permissions: PermissionResponse[];
  teams: TeamId[];
  teamsNames: string[];
  deletedAt: number | null;
  emails?: string[];
  customFields?: { [key: string]: CustomFieldResponseType };

  externalAccountId: string;
  externalAccountUrl: string;
  externalProductId: string;

  accountSettings: AccountSettingsResponse;

  twoFactor: boolean;
  twoFactorEmail: boolean;
  twoFactorSMS: boolean;
  twoFactorAPP: boolean;

  slug: string;

  clockinEnabled?: boolean;
  manualTimeEnabled?: boolean;
  maximumHours?: string;
  allowOvertime?: boolean;
  ovetimeHours?: string;

  timezone?: string;
  openings?: UserOpeningResponse[];

  calendarSettings?: {
    duration: number;
    futureLimit: number;
    minBookingNotice: number;
    bufferTime: number;
    title: string;
  };
  permissionAll?: boolean;

  startDate: number;
  endDate: number;
  userType: UserType;

  expectedCloseEmail: boolean;
  overLimitDailyEmail: boolean;
  weeklyLimitReachingEmail: boolean;
  orgId: OrgId;
  notifications: { [key in string]: boolean };
  contactId: ContactId;
  provider?: VeryCompactProviderResponse;
  geoJson?: any | null;
}

export interface UserOpeningResponse {
  days: string[];
  start: string;
  end: string;
}

export type CompactUserResponse = Pick<
  UserResponse,
  'id' | 'firstName' | 'lastName' | 'email' | 'salary' | 'jobTitle' | 'role' | 'deletedAt' | 'phone'
> & {
  provider?: VeryCompactProviderResponse;
};
export interface VeryCompactUserResponse {
  id: UserId;
  name: string;
  regionId?: RegionId;
}

export interface UserSocialAccountResponse {
  id: UserSocialId;
  userId: UserId;
  email: string;
  name: string;
  signature: string;
  provider: SocialAccountProviders;
  accountId: string;
  status: SocialAccountStatus;
  schedulingLink: string;
  primaryCalendar: string;
}

export enum SocialAccountProviders {
  gmail = 'gmail',
  eas = 'eas',
  outlook = 'outlook',
  yahoo = 'yahoo',
  icloud = 'icloud',
  custom = 'custom',
}

export enum SocialAccountStatus {
  active = 'active',
  stopped = 'stopped',
  invalid = 'invalid',
}

export interface ChannelSignatureResponse {
  channelId: string;
  signature: string;
}

export interface SignupRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface LoginActivityResponse extends BaseResponse {
  id: string;
  ip: string;
}

export interface EmailItemResponse {
  email: string;
  name: string;
}

export interface TwoFactorResponse {
  token: string;
  status: boolean;
  code: string;
  type: TwoFactorType;
}

export interface TwoFactorEnableResponse {
  status: boolean;
  email: string;
  truncatedEmail: string;
  phone: string;
  availableTypes: TwoFactorType[];
}

export enum TwoFactorType {
  SMS = 'sms',
  EMAIL = 'email',
  PHONE = 'phone',
  APP = 'app',
}

export type ImpersonateResponse = Pick<UserResponse, 'firstName' | 'lastName' | 'email'> & {
  backUrl: string;
  isLogin: boolean;
};

export interface RoleCount {
  role: UserRole;
  total: number;
}

export interface UserParams extends PageQuery {
  page?: number;
  isStaff?: boolean;
  role?: UserRole;
  sortBy?: string;
  search?: string;
  // search?: string;
}
